import React from 'react';
import { Container, Row, Col } from 'reactstrap';
import cx from 'classnames';

import Link from './Link';
import Image from './Image';
import Content from './Content';

export const AlternatingItemsSection = ({ section, contentComponent }) => {
  const PageContent = contentComponent || Content; // needed to cooperate with Netlify CMS preview

  return (
    <Container>
      <Row>
        <Col xs={12}>
          <div className="text-container mx-auto">
            {section.header && <h1 className="mb-4">{section.header}</h1>}
            {section.description && (
              <div className="mb-7">
                <PageContent
                  className="page-content"
                  content={section.description}
                />
              </div>
            )}
          </div>
        </Col>
      </Row>
      <Row>
        <Col md={8} className="mx-md-auto">
          {section.items.map((item, i) => (
            <Row
              key={i}
              className={i === section.items.length - 1 ? undefined : 'mb-5'}
            >
              <Col
                md={{ order: i % 2 === 0 ? 1 : 2, size: 3 }}
                className="d-flex justify-content-center align-items-center"
              >
                <div className="circle-num-100 mb-4 mb-md-0">{i + 1}</div>
              </Col>
              <Col key={i} md={{ order: i % 2 === 0 ? 2 : 1, size: 9 }}>
                <div>
                  <h2>{item.header}</h2>
                  <PageContent
                    className="page-content"
                    content={item.description}
                  />
                </div>
              </Col>
            </Row>
          ))}
        </Col>
      </Row>
    </Container>
  );
};

export const ThreeColumnSection = ({ section, contentComponent }) => {
  const PageContent = contentComponent || Content; // needed to cooperate with Netlify CMS preview

  return (
    <Container fluid>
      <Row>
        <Col xs={12}>
          <div
            className={cx({
              'mx-auto text-center': section.centered_text,
              'text-container': !section.centered_text,
            })}
          >
            {section.image && (
              <Image
                className="mb-6 section-header-image rounded"
                image={section.image}
              />
            )}
            {section.header && (
              <h1
                className={cx('mb-5', {
                  'text-center': section.centered_text,
                })}
              >
                <PageContent content={section.header} />
              </h1>
            )}
            {section.description && (
              <div
                className={cx('mb-4', {
                  'text-container mx-auto': section.centered_text,
                })}
              >
                <PageContent
                  className="page-content"
                  content={section.description}
                />
              </div>
            )}
          </div>
        </Col>
      </Row>
      <Row className="justify-content-center">
        {section.items.map((item, i) => (
          <Col
            key={i}
            md={4}
            className={cx('d-flex', {
              'text-center': section.centered_text,
            })}
          >
            <div
              className={cx(
                'd-flex flex-column align-items-center justify-content-between w-100 px-4 px-md-2 mb-md-0 mw-500 mx-auto',
                {
                  'mb-8': i < section.items.length - 1,
                  'mb-3': i === section.items.length - 1,
                }
              )}
            >
              <div className="mb-3 w-100">
                {item.image && (
                  <Image
                    className="mb-5 section-image rounded"
                    image={item.image}
                  />
                )}
                {item.header && (
                  <h2
                    className={cx({
                      'purple-item-headers':
                        section.meta === 'purple-item-headers',
                    })}
                  >
                    {item.header}
                  </h2>
                )}
                {item.description && (
                  <PageContent
                    className="page-content"
                    content={item.description}
                  />
                )}
              </div>
              {item.call_to_action && (
                <Link
                  to={item.call_to_action_url}
                  className="mt-4 btn btn-white btn-important"
                >
                  {item.call_to_action}
                </Link>
              )}
            </div>
          </Col>
        ))}
      </Row>
    </Container>
  );
};

export const ItemsSection = ({ section, contentComponent }) => {
  const PageContent = contentComponent || Content; // needed to cooperate with Netlify CMS preview

  return (
    <Container>
      <Row>
        <Col xs={12}>
          <div
            className={cx({
              'mx-auto text-center': section.centered_text,
              'text-container mx-auto': !section.centered_text,
            })}
          >
            {section.header && (
              <h1
                className={cx('mb-4', {
                  'text-center': section.centered_text,
                })}
              >
                {section.header}
              </h1>
            )}
            {section.description && (
              <div className="mb-7">
                <PageContent
                  className="page-content"
                  content={section.description}
                />
              </div>
            )}
          </div>
          <div className="text-container mx-auto">
            {section.items.map((item, i) => (
              <div
                key={i}
                className={i === section.items.length - 1 ? undefined : 'mb-6'}
              >
                {item.image && (
                  <Image
                    className="mb-4 mt-6 section-image rounded"
                    image={item.image}
                    title={item.image_caption}
                  />
                )}
                <h2>{item.header}</h2>
                <PageContent
                  className="page-content"
                  content={item.description}
                />
              </div>
            ))}
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export const LvnOverview = ({ items }) => {
  /**
   * NOTE: this has two versions for different responsive breakpoints.
   * Make sure you update both!
   */

  return (
    <div className="lvn-overview">
      <svg className="mx-auto d-block d-md-none mb-6" width={300} height={300}>
        <circle className="circle-1" cx="150" cy="150" r="150" />
        <circle className="circle-2" cx="150" cy="150" r="95" />
        <circle className="circle-3" cx="150" cy="150" r="40" />
      </svg>
      <div className="d-block d-md-none mx-auto text-center text-container mw-500">
        <div className="mb-6">
          <h2 className={'purple-item-headers'}>
            <svg
              viewBox="0 0 34 34"
              width="0.9em"
              height="0.9em"
              className="mb-2 header-circles mx-auto d-block"
            >
              <g transform="translate(1, 1)">
                <circle className="circle-1" cx="15" cy="15" r="15" />
                <circle className="circle-2" cx="15" cy="15" r="9.5" />
                <circle className="circle-3" cx="15" cy="15" r="4" />
              </g>
            </svg>
            PUBLIC
          </h2>
          <div>
            Listening channel for journalists, policy makers, and community at
            large
          </div>
        </div>
        <div className="mb-6">
          <h2 className={'purple-item-headers'}>
            <svg
              viewBox="0 0 34 34"
              width="0.9em"
              height="0.9em"
              className="mb-2 header-circles mx-auto d-block"
            >
              <g transform="translate(1, 1)">
                <circle className="circle-1 no-fill" cx="15" cy="15" r="15" />
                <circle className="circle-2 no-fill" cx="15" cy="15" r="9.5" />
                <circle className="circle-3" cx="15" cy="15" r="4" />
              </g>
            </svg>
            CONVERSATION
          </h2>
          <div>Community-led facilitated dialogue</div>
        </div>
        <div className="mb-6">
          <h2 className={'purple-item-headers'}>
            <svg
              viewBox="0 0 34 34"
              width="0.9em"
              height="0.9em"
              className="mb-2 header-circles mx-auto d-block"
            >
              <g transform="translate(1, 1)">
                <circle className="circle-1 no-fill" cx="15" cy="15" r="15" />
                <circle className="circle-2" cx="15" cy="15" r="9.5" />
                <circle className="circle-3" cx="15" cy="15" r="4" />
              </g>
            </svg>
            NETWORK
          </h2>
          <div>Connect hosts and conversations</div>
        </div>
      </div>
      <svg className="mx-auto d-none d-md-block" width={700} height={430}>
        <g transform="translate(0, 30)">
          <g transform="translate(420, -2)">
            <g transform="translate(0, 0)">
              <text className="lvn-overview-title">PUBLIC</text>
              <text className="lvn-overview-desc" transform="translate(0, 30)">
                Listening channel for
                <tspan x="0" y="30">
                  journalists, policy makers,
                </tspan>
                <tspan x="0" y="60">
                  and community at large
                </tspan>
              </text>
            </g>
            <g transform="translate(0, 180)">
              <text className="lvn-overview-title">CONVERSATION</text>
              <text className="lvn-overview-desc" transform="translate(0 30)">
                Community-led facilitated
                <tspan x="0" y="30">
                  dialogue
                </tspan>
              </text>
            </g>
            <g transform="translate(0, 332)">
              <text className="lvn-overview-title">NETWORK</text>
              <text className="lvn-overview-desc" transform="translate(0, 30)">
                Connect hosts and
                <tspan x="0" y="30">
                  conversations
                </tspan>
              </text>
            </g>
          </g>

          <circle className="circle-1" cx="150" cy="168" r="150" />
          <circle className="circle-2" cx="150" cy="168" r="95" />
          <circle className="circle-3" cx="150" cy="168" r="40" />
          <path
            className="line-2 public-path"
            d="M212 31 L280 -12 L405 -12"
            x1="212"
            y1="13"
            x2="410"
            y2="-12"
          />
          <path
            className="line-2 conv-path"
            d="M185 168 L280 168 L405 168"
            x1="185"
            y1="133"
            x2="410"
            y2="138"
          />
          <path
            className="line-2 network-path"
            d="M201 248 L280 320 L405 320"
            x1="201"
            y1="230"
            x2="410"
            y2="260"
          />
        </g>
      </svg>
    </div>
  );
};

export const CustomSection = ({ section, contentComponent }) => {
  const PageContent = contentComponent || Content; // needed to cooperate with Netlify CMS preview
  let inner = null;
  if (section.meta === 'lvn-overview') {
    inner = (
      <LvnOverview items={section.items} contentComponent={PageContent} />
    );
  }
  return (
    <Container>
      <Row>
        <Col xs={12}>{inner}</Col>
      </Row>
    </Container>
  );
};

export const BasicSection = ({ section, contentComponent }) => {
  const PageContent = contentComponent || Content; // needed to cooperate with Netlify CMS preview
  return (
    <Container>
      <Row>
        <Col xs={12}>
          <div
            className={cx({
              'mx-auto text-center': section.centered_text,
              'text-container mx-auto': !section.centered_text,
            })}
          >
            {section.image && (
              <Image
                className="mb-6 section-header-image rounded"
                image={section.image}
              />
            )}
            {section.pre_header && (
              <div className="pre-header mb-2">{section.pre_header}</div>
            )}
            {section.header && (
              <h1
                className={cx('mb-4', {
                  'text-center': section.centered_text,
                })}
              >
                {section.header}
              </h1>
            )}
            {section.description && (
              <div
                className={cx({
                  'text-container mx-auto': section.centered_text,
                  'mb-6': section.call_to_action,
                })}
              >
                <PageContent
                  className="page-content"
                  content={section.description}
                />
              </div>
            )}
            {section.call_to_action && (
              <Link
                to={section.call_to_action_url}
                className={cx('btn btn-important', {
                  'btn-primary': section.bg !== 'purple',
                  'btn-white': section.bg === 'purple',
                })}
              >
                {section.call_to_action}
              </Link>
            )}
          </div>
        </Col>
      </Row>
    </Container>
  );
};

export const Section = ({ section, contentComponent, className }) => {
  const PageContent = contentComponent || Content; // needed to cooperate with Netlify CMS preview
  if (!section) {
    return null;
  }
  let inner = 'TODO';
  if (section.type === 'alternating-items') {
    inner = (
      <AlternatingItemsSection
        section={section}
        contentComponent={PageContent}
      />
    );
  } else if (section.type === 'three-column') {
    inner = (
      <ThreeColumnSection section={section} contentComponent={PageContent} />
    );
  } else if (section.type === 'items') {
    inner = <ItemsSection section={section} contentComponent={PageContent} />;
  } else if (section.type === 'basic') {
    inner = <BasicSection section={section} contentComponent={PageContent} />;
  } else if (section.type === 'custom') {
    inner = <CustomSection section={section} contentComponent={PageContent} />;
  }

  return (
    <div
      id={section.id}
      className={cx('section', className, {
        'section-bg': !!section.bg,
        [`bg-${section.bg}`]: section.bg != null,
      })}
    >
      {inner}
    </div>
  );
};

export default Section;
